import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { supabase } from '../../../supabaseConfig';
import classes from './ManageIdCards.module.css';
import Loader from '../helpers/Loader/Loader';
import Modal from '../helpers/Modal/Modal';
import sign from '../../../components/signature.png';
import '../../../components/IdCard.css';

const ManageIdCards = () => {
  const [idCards, setIdCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [idCardData, setIdCardData] = useState(null);

  const fetchIdCards = async () => {
    setLoading(true);
    try {
      const idCardsCollection = collection(db, 'idCards');
      const idCardDocs = await getDocs(idCardsCollection);
      const idCardsData = idCardDocs.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })).sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically
      setIdCards(idCardsData);
    } catch (error) {
      console.error('Error fetching ID cards:', error);
      setErrorMessage('Failed to fetch ID cards');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIdCards();
  }, []);

  const handleCardSelect = (card) => {
    setSelectedCard(card);
    setIdCardData({ ...card, photo: null });
    setErrorMessage('');
    setSuccessMessage('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrorMessage('File size should be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setErrorMessage('Please select an image file');
        return;
      }
      setIdCardData(prev => ({ ...prev, photo: file, photoUrl: URL.createObjectURL(file) }));
      setErrorMessage('');
    }
  };

  const uploadImageToSupabase = async (file, studentRegNumber) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${studentRegNumber}_${Date.now()}.${fileExt}`;
    const { error } = await supabase.storage.from('id-cards').upload(fileName, file);
    if (error) throw error;
    const { data } = supabase.storage.from('id-cards').getPublicUrl(fileName);
    return data.publicUrl;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCard) return;

    setUploading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      let photoUrl = idCardData.photoUrl;
      if (idCardData.photo) {
        photoUrl = await uploadImageToSupabase(idCardData.photo, selectedCard.studentRegNumber);
      }

      const idCardRef = doc(db, 'idCards', selectedCard.studentRegNumber);
      const updatedData = { ...idCardData, photoUrl, updatedAt: new Date().toISOString() };
      delete updatedData.photo;
      delete updatedData.id;

      await setDoc(idCardRef, updatedData, { merge: true });

      const updatedCards = idCards.map(card =>
        card.id === selectedCard.id ? { ...updatedData, id: selectedCard.id } : card
      );
      setIdCards(updatedCards);
      setSelectedCard({ ...updatedData, id: selectedCard.id });

      setSuccessMessage('ID Card updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating ID card:', error);
      setErrorMessage(error.message || 'Failed to update ID card');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCard || !window.confirm(`Are you sure you want to delete the ID card for ${selectedCard.name}?`)) return;

    setDeleting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await deleteDoc(doc(db, 'idCards', selectedCard.studentRegNumber));
      try {
        await deleteDoc(doc(db, 'idCardApplications', selectedCard.studentRegNumber));
      } catch (appDeleteError) {
        console.warn(`Could not delete application for ${selectedCard.studentRegNumber}:`, appDeleteError);
      }

      const updatedCards = idCards.filter(card => card.id !== selectedCard.id);
      setIdCards(updatedCards);
      setSelectedCard(null);
      setSearchTerm('');

      setSuccessMessage('ID Card deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting ID card:', error);
      setErrorMessage(error.message || 'An unexpected error occurred.');
    } finally {
      setDeleting(false);
    }
  };

  const filteredCards = idCards.filter(card => {
    const name = String(card.name || '');
    const regNumber = String(card.studentRegNumber || '');
    const term = searchTerm.toLowerCase();
    return name.toLowerCase().includes(term) || regNumber.toLowerCase().includes(term);
  });

  return (
    <div className={classes.container}>
      <header className={classes.header}>
        <h2 className={classes.heading}>Manage Student ID Cards</h2>
        <input
          type="text"
          className={classes.searchInput}
          placeholder="Search by name or registration number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </header>

      {(loading || deleting || uploading) && <Modal><Loader /></Modal>}
      
      <div className={classes.mainLayout}>
        <aside className={classes.cardsList}>
          {filteredCards.length > 0 ? filteredCards.map((card) => (
            <div
              key={card.id}
              className={`${classes.cardItem} ${selectedCard?.id === card.id ? classes.selected : ''}`}
              onClick={() => handleCardSelect(card)}
            >
              <strong>{card.name}</strong>
              <span>{card.studentRegNumber}</span>
            </div>
          )) : <div className={classes.placeholder}>No cards found.</div>}
        </aside>

        <main className={classes.detailsPanel}>
          {!selectedCard ? (
            <div className={classes.placeholder}>Select a card to view and manage its details.</div>
          ) : (
            <div>
              <div className={classes.cardPreview}>
                <div className="id-card horizontal" style={{ margin: '0 auto', transform: 'scale(0.8)' }}>
                  <div className="header">
                    <div className="logo">
                      <img src="/numl.png" alt="NUML Logo" crossOrigin="anonymous" />
                    </div>
                    <div className="meow">
                      <div className="header-text">
                        NATIONAL UNIVERSITY OF MODERN LANGUAGES
                      </div>
                      <div className="sub-header">Lahore Campus</div>
                    </div>
                  </div>
                  <div className="contact">
                    <div className="tel">Tel: 042-99204588</div>
                    <div className="web">www.numl.edu.pk</div>
                  </div>
                  <div className="info">
                    <div className="details">
                      <div><b>Name:</b> {idCardData.name}</div>
                      <div><b>Class:</b> {idCardData.className}</div>
                      <div><b>SYS ID:</b> {idCardData.sysId}</div>
                      <div><b>Session:</b> {idCardData.session}</div>
                      <div><b>CNIC #:</b> {idCardData.cnic}</div>
                    </div>
                    <div className="photo">
                      {idCardData.photoUrl ? <img src={idCardData.photoUrl} alt="Student" crossOrigin="anonymous" /> : <div className="no-photo">No Photo</div>}
                    </div>
                  </div>
                  <div className="footer">
                    <div className="signature">
                      <img src={sign} alt="Signature" crossOrigin="anonymous" />
                    </div>
                    Regional Director
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className={classes.form}>
                <div className={classes.formGrid}>
                  <div className={classes.formGroup}><label>Name</label><input type="text" value={idCardData.name} onChange={(e) => setIdCardData(p => ({...p, name: e.target.value}))} className={classes.input} required /></div>
                  <div className={classes.formGroup}><label>Class/Program</label><input type="text" value={idCardData.className} onChange={(e) => setIdCardData(p => ({...p, className: e.target.value}))} className={classes.input} required /></div>
                  <div className={classes.formGroup}><label>SYS ID</label><input type="text" value={idCardData.sysId} onChange={(e) => setIdCardData(p => ({...p, sysId: e.target.value}))} className={classes.input} required /></div>
                  <div className={classes.formGroup}><label>Session</label><input type="text" value={idCardData.session} onChange={(e) => setIdCardData(p => ({...p, session: e.target.value}))} className={classes.input} required /></div>
                  <div className={classes.formGroup}><label>CNIC #</label><input type="text" value={idCardData.cnic} onChange={(e) => setIdCardData(p => ({...p, cnic: e.target.value}))} className={classes.input} required /></div>
                  <div className={classes.formGroup}><label>Student Photo</label><input type="file" accept="image/*" onChange={handleFileChange} className={classes.fileInput} /></div>
                </div>
                <div className={classes.formActions}>
                    <button type="submit" className={classes.submitButton} disabled={uploading}>{uploading ? 'Updating...' : 'Update Card'}</button>
                    <button type="button" onClick={handleDelete} className={classes.deleteButton} disabled={deleting}>{deleting ? 'Deleting...' : 'Delete Card'}</button>
                </div>
              </form>
            </div>
          )}
        </main>
      </div>
      {successMessage && <div className={`${classes.message} ${classes.successMessage}`}>{successMessage}</div>}
      {errorMessage && <div className={`${classes.message} ${classes.errorMessage}`}>{errorMessage}</div>}
    </div>
  );
};

export default ManageIdCards;
